/**
 * Returns all focusable elements within a container.
 * @param {HTMLElement} container
 * @returns {HTMLElement[]}
 */
const getFocusableElements = (container) =>
  Array.from(
    container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), details > summary',
    ),
  ).filter((el) => !el.closest('[inert]'));

/**
 * Sets `inert` on all siblings of the modal and siblings of its ancestors up to <body>.
 * @param {HTMLElement} excludeEl — the element to keep interactive
 * @param {HTMLElement|null} triggerElement — the element that triggered the open (for focus return on close)
 */
const setPageInert = (excludeEl, triggerElement) => {
  let current = excludeEl;
  while (current && current !== document.body) {
    const parent = current.parentElement;
    if (!parent) break;
    Array.from(parent.children).forEach((sibling) => {
      if (
        sibling !== current &&
        sibling !== triggerElement &&
        !['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName) &&
        !sibling.hasAttribute('inert')
      ) {
        sibling.setAttribute('inert', '');
        sibling.setAttribute('data-a11y-inert', 'true');
      }
    });
    current = parent;
  }
};

/**
 * Removes `inert` from all elements that were dynamically inerted.
 */
const removePageInert = () => {
  document.querySelectorAll('[data-a11y-inert]').forEach((el) => {
    el.removeAttribute('inert');
    el.removeAttribute('data-a11y-inert');
  });
};

/**
 * Removes the dynamic `inert` from a modal and its ancestors. A modal opened
 * over another one (e.g. the localization drawer from the mobile menu) sits in
 * a section the first modal made inert, and would show up unclickable.
 * @param {HTMLElement} modalEl
 */
const releaseInert = (modalEl) => {
  for (let el = modalEl; el && el !== document.body; el = el.parentElement) {
    if (el.hasAttribute('data-a11y-inert')) {
      el.removeAttribute('inert');
      el.removeAttribute('data-a11y-inert');
    }
  }
};
/**
 * Opens a modal/drawer.
 * @param {HTMLElement|string} modal — the modal element or a CSS selector
 * @param {HTMLElement|null} triggerElement — the element that triggered the open (for focus return on close)
 */
const getTransitionDuration = (el) => {
  const raw = getComputedStyle(el).transitionDuration.split(',')[0].trim();
  return raw.endsWith('ms') ? parseFloat(raw) : parseFloat(raw) * 1000;
};

const openModal = (modal, triggerElement = null) => {
  if (!modal) return;
  const foundModal = typeof modal === 'string' ? document.querySelector(modal) : modal;
  if (!foundModal) return;

  foundModal._a11yTrigger =
    triggerElement ?? (document.activeElement !== document.body ? document.activeElement : null);

  document.documentElement.style.overflow = 'hidden';

  window.requestAnimationFrame(() => {
    foundModal.classList.add('open');
    releaseInert(foundModal);
    setPageInert(foundModal, triggerElement);

    const focusable = getFocusableElements(foundModal);
    if (focusable.length > 0) focusable[0].focus();
    else if (foundModal.getAttribute('tabindex')) foundModal.focus();

    foundModal._a11yEscHandler = (e) => {
      if (e.key === 'Escape') closeModal(foundModal);
    };

    // Focus trap: cycle Tab/Shift+Tab within the modal
    foundModal._a11yTrapHandler = (e) => {
      if (e.key !== 'Tab') return;

      const focusable = getFocusableElements(foundModal);
      if (!focusable.length) {
        e.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', foundModal._a11yEscHandler);
    document.addEventListener('keydown', foundModal._a11yTrapHandler);
  });
};

const closeModal = (modal) => {
  if (!modal) return;
  const foundModal = typeof modal === 'string' ? document.querySelector(modal) : modal;
  if (!foundModal) return;

  if (foundModal._a11yEscHandler) {
    document.removeEventListener('keydown', foundModal._a11yEscHandler);
    foundModal._a11yEscHandler = null;
  }

  if (foundModal._a11yTrapHandler) {
    document.removeEventListener('keydown', foundModal._a11yTrapHandler);
    foundModal._a11yTrapHandler = null;
  }

  const duration = getTransitionDuration(foundModal);
  window.requestAnimationFrame(() => {
    foundModal.classList.remove('open');
    setTimeout(() => {
      removePageInert();
      // Back to the modal underneath, if any: it keeps the page locked and inert
      const underneath = [...document.querySelectorAll('.modal.open')].find((el) => el !== foundModal);
      if (underneath) setPageInert(underneath, underneath._a11yTrigger);
      else document.documentElement.style.overflow = '';
      foundModal._a11yTrigger?.focus();
      foundModal._a11yTrigger = null;
    }, duration);
  });
};

const initModals = (root = document) => {
  if (!root) return;
  const rootEl = typeof root === 'string' ? document.querySelector(root) : root;
  if (!rootEl) return;

  rootEl.querySelectorAll('.modal').forEach((modal) => {
    modal.querySelectorAll('.modal__backdrop').forEach((el) => el.addEventListener('click', () => closeModal(modal)));

    modal
      .querySelectorAll('.modal__close, .modal__refuse')
      .forEach((el) => el.addEventListener('click', () => closeModal(modal)));

    if (modal.id) {
      rootEl.querySelectorAll(`a[href*="${modal.id}"]`).forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          openModal(modal, btn);
        }),
      );
    }
  });
};

export { initModals, openModal, closeModal };
